import { useIntl } from "react-intl";
import type { FieldOption } from "@/lib/field-util.ts";
import { Select } from "./Select";

// Month + Year picker. Wire format: 'YYYY-MM' (or just 'YYYY' when month
// is unset). Accepts 'YYYY-MM-DD' as input - day is ignored.

const THIS_YEAR = new Date().getFullYear();
const YEAR_OPTIONS: readonly FieldOption[] = Array.from({ length: 80 }, (_, i) => {
  const y = String(THIS_YEAR - i);
  return { value: y, label: y };
});

interface MonthYearSelectProps {
  value: string | null | undefined;
  onChange: (next: string) => void;
  accent?: boolean;
  className?: string;
  // Optional DOM id base for e2e tests (spark-validation): the two child
  // selects become `${id}-month` and `${id}-year`.
  id?: string;
}

export function MonthYearSelect({ value, onChange, accent = false, className = "", id }: MonthYearSelectProps) {
  const intl = useIntl();
  // Locale-correct month names via the active locale's date formatter.
  const monthOptions: readonly FieldOption[] = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1).padStart(2, "0"),
    label: intl.formatDate(new Date(2020, i, 1), { month: "long" }),
  }));
  const [yPart = "", mPart = ""] = String(value ?? "").split("-");
  const ring = accent ? "ring-accent-decoration/60" : "";
  // Sentinel `-MM` for month-only state. Without this, picking the month
  // before the year silently discards the selection (combine produced "")
  // and the dropdown snapped back to the placeholder. The sentinel is
  // invalid by design - toDateInstant rejects it, so validation still
  // forces the user to fill the year before the row can be saved.
  const combine = (y: string, m: string) => {
    if (!y && !m) return onChange("");
    if (!y) return onChange(`-${m}`);
    if (!m) return onChange(y);
    onChange(`${y}-${m}`);
  };

  return (
    <div className={`flex gap-2 ${className}`}>
      <Select
        id={id ? `${id}-month` : undefined}
        value={mPart}
        onChange={(m) => combine(yPart, m)}
        options={monthOptions}
        placeholder={intl.formatMessage({ id: "design.monthPlaceholder", defaultMessage: "Month" })}
        className={`flex-1 ${ring}`}
      />
      <Select
        id={id ? `${id}-year` : undefined}
        value={yPart}
        onChange={(y) => combine(y, mPart)}
        options={YEAR_OPTIONS}
        placeholder={intl.formatMessage({ id: "design.yearPlaceholder", defaultMessage: "Year" })}
        className={`flex-1 ${ring}`}
      />
    </div>
  );
}
